import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { api, toast } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

const WBSMasterList = ({ user }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState([]);
  const [filteredEntries, setFilteredEntries] = useState([]);
  const [activeTab, setActiveTab] = useState('Structural');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const [formData, setFormData] = useState({
    type: 'Structural',
    item: '',
    complexity: 'Simple',
    hours_modelling: 0,
    hours_detailing: 0,
    hours_erection: 0,
    hours_support: 0,
  });

  useEffect(() => {
    fetchEntries();
  }, []);

  useEffect(() => {
    filterEntries();
  }, [entries, activeTab, searchQuery]);

  const fetchEntries = async () => {
    try {
      setLoading(true);
      const response = await api.get('/wbs/master-list');
      setEntries(response.data);
    } catch (error) {
      console.error('Failed to fetch WBS master list:', error);
      toast.error('Failed to load WBS master list');
    } finally {
      setLoading(false);
    }
  };

  const filterEntries = () => {
    let filtered = entries.filter(entry => entry.type === activeTab);
    
    if (searchQuery) {
      filtered = filtered.filter(entry =>
        entry.item.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Sort by item name
    filtered.sort((a, b) => {
      if (a.item < b.item) return -1;
      if (a.item > b.item) return 1;
      return a.complexity.localeCompare(b.complexity);
    });

    setFilteredEntries(filtered);
  };

  const handleCreate = () => {
    setEditMode(false);
    setEditingId(null);
    setFormData({
      type: activeTab,
      item: '',
      complexity: 'Simple',
      hours_modelling: 0,
      hours_detailing: 0,
      hours_erection: 0,
      hours_support: 0,
    });
    setDialogOpen(true);
  };

  const handleEdit = (entry) => {
    setEditMode(true);
    setEditingId(entry.id);
    setFormData({
      type: entry.type,
      item: entry.item,
      complexity: entry.complexity,
      hours_modelling: entry.hours_modelling,
      hours_detailing: entry.hours_detailing,
      hours_erection: entry.hours_erection,
      hours_support: entry.hours_support,
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.item.trim()) {
      toast.error('Item name is required');
      return;
    }

    try {
      if (editMode) {
        await api.put(`/wbs/master-list/${editingId}`, formData);
        toast.success('WBS entry updated successfully');
      } else {
        await api.post('/wbs/master-list', formData);
        toast.success('WBS entry created successfully');
      }

      setDialogOpen(false);
      fetchEntries();
    } catch (error) {
      console.error('Failed to save WBS entry:', error);
      toast.error(error.response?.data?.detail || 'Failed to save WBS entry');
    }
  };

  const handleDelete = async (id, item) => {
    if (!window.confirm(`Are you sure you want to delete "${item}"?`)) {
      return;
    }

    try {
      await api.delete(`/wbs/master-list/${id}`);
      toast.success('WBS entry deleted successfully');
      fetchEntries();
    } catch (error) {
      console.error('Failed to delete WBS entry:', error);
      toast.error('Failed to delete WBS entry');
    }
  };

  const getComplexityBadge = (complexity) => {
    const colors = {
      Simple: 'bg-green-100 text-green-800',
      Medium: 'bg-yellow-100 text-yellow-800',
      Complex: 'bg-red-100 text-red-800',
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[complexity]}`}>
        {complexity}
      </span>
    );
  };

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="text-lg">Loading...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user}>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-md3-display-small">WBS Master List</h1>
            <p className="text-md3-on-surface-variant mt-1">Manage Work Breakdown Structure master entries</p>
          </div>
          <Button className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" onClick={handleCreate}>
            Add WBS Entry
          </Button>
        </div>

        <Card className="bg-md3-surface-container shadow-md3-1 hover:shadow-md3-2 transition-shadow duration-200 rounded-md3-lg border-0">
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle className="text-md3-title-large text-md3-on-surface ">Master List Entries</CardTitle>
              <Input className="w-64 bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20" placeholder="Search by item name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="mb-4">
                <TabsTrigger value="Structural">Structural</TabsTrigger>
                <TabsTrigger value="Miscellaneous">Miscellaneous</TabsTrigger>
              </TabsList>

              <TabsContent value={activeTab}>
                <div className="rounded-md3-md border">
                  <Table className="bg-md3-surface-container rounded-md3-md">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Item</TableHead>
                        <TableHead>Complexity</TableHead>
                        <TableHead className="text-right">Modelling (min)</TableHead>
                        <TableHead className="text-right">Detailing (min)</TableHead>
                        <TableHead className="text-right">Erection (min)</TableHead>
                        <TableHead className="text-right">Support (min)</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredEntries.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center text-md3-on-surface-variant py-8">
                            No entries found. Click &quot;Add WBS Entry&quot; to create one.
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredEntries.map((entry) => (
                          <TableRow key={entry.id}>
                            <TableCell className="font-medium">{entry.item}</TableCell>
                            <TableCell>{getComplexityBadge(entry.complexity)}</TableCell>
                            <TableCell className="text-right">{entry.hours_modelling}</TableCell>
                            <TableCell className="text-right">{entry.hours_detailing}</TableCell>
                            <TableCell className="text-right">{entry.hours_erection}</TableCell>
                            <TableCell className="text-right">{entry.hours_support}</TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-2">
                                <Button variant="outline" className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full" size="sm"
                                   onClick={() => handleEdit(entry)}>
                                  Edit
                                </Button>
                                <Button variant="destructive"
                                  size="sm"
                                  className="bg-red-600 hover:bg-red-700 text-white shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full"
                                  onClick={() => handleDelete(entry.id, entry.item)}>
                                  Delete
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* Add/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-2xl bg-white">
            <DialogHeader>
              <DialogTitle className="text-md3-headline-small text-md3-on-surface font-semibold ">
                {editMode ? 'Edit WBS Entry' : 'Add WBS Entry'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-2 gap-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="type" className="text-md3-label-large text-gray-700 font-medium">Type *</Label>
                  <Select
                    value={formData.type}
                    onValueChange={(value) => setFormData({ ...formData, type: value })}
                    disabled={editMode}
                  >
                    <SelectTrigger className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Structural">Structural</SelectItem>
                      <SelectItem value="Miscellaneous">Miscellaneous</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="item" className="text-md3-label-large text-gray-700 font-medium">Item *</Label>
                  <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="item"
                    value={formData.item}
                    onChange={(e) => setFormData({ ...formData, item: e.target.value })}
                    placeholder="e.g., Foundation, Column"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="complexity" className="text-md3-label-large text-gray-700 font-medium">Complexity *</Label>
                  <Select
                    value={formData.complexity}
                    onValueChange={(value) => setFormData({ ...formData, complexity: value })}
                  >
                    <SelectTrigger className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Simple">Simple</SelectItem>
                      <SelectItem value="Medium">Medium</SelectItem>
                      <SelectItem value="Complex">Complex</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="col-span-2 border-t pt-4 mt-2">
                  <h4 className="font-semibold mb-3">Hours (in minutes)</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="hours_modelling" className="text-md3-label-large text-gray-700 font-medium">Modelling</Label>
                      <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 no-spinner" id="hours_modelling"
                        type="number"
                        min="0"
                        
                        value={formData.hours_modelling || ''}
                        onChange={(e) => setFormData({ ...formData, hours_modelling: parseInt(e.target.value) || 0 })}
                        placeholder="Minutes"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="hours_detailing" className="text-md3-label-large text-gray-700 font-medium">Detailing</Label>
                      <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 no-spinner" id="hours_detailing"
                        type="number"
                        min="0"
                        
                        value={formData.hours_detailing || ''}
                        onChange={(e) => setFormData({ ...formData, hours_detailing: parseInt(e.target.value) || 0 })}
                        placeholder="Minutes"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="hours_erection" className="text-md3-label-large text-gray-700 font-medium">Erection</Label>
                      <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 no-spinner" id="hours_erection"
                        type="number"
                        min="0"
                        
                        value={formData.hours_erection || ''}
                        onChange={(e) => setFormData({ ...formData, hours_erection: parseInt(e.target.value) || 0 })}
                        placeholder="Minutes"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="hours_support" className="text-md3-label-large text-gray-700 font-medium">Support</Label>
                      <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 no-spinner" id="hours_support"
                        type="number"
                        min="0"
                        
                        value={formData.hours_support || ''}
                        onChange={(e) => setFormData({ ...formData, hours_support: parseInt(e.target.value) || 0 })}
                        placeholder="Minutes"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" >
                  {editMode ? 'Update' : 'Create'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default WBSMasterList;
